import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { MessageCircle, Phone, Mail, MapPin, Briefcase, Plus, Coins, Star, Filter, Zap, RefreshCw, CheckCircle, Clock, Shield } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table } from '../components/ui/table';
import { toast } from 'sonner';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import FloatingActionButton from '../components/FloatingActionButton';
import ContactUsButton from '../components/ContactUsButton';
import ReviewsListModal from '../components/ReviewsListModal';
import ApplicantDetailModal from '../components/ApplicantDetailModal';
import PastHiresSection from '../components/PastHiresSection';
import ReferralSection from '../components/ReferralSection';
import JobPostingsTable from '../components/JobPostingsTable';
import MyBannersSection from '../components/MyBannersSection';
import MyServiceListingsSection from '../components/MyServiceListingsSection';
import MyServiceRequestsSection from '../components/MyServiceRequestsSection';
import MatchingRequestsSection from '../components/MatchingRequestsSection';
import ProCredentialsSection from '../components/ProCredentialsSection';
import PendingReviewsCard from '../components/PendingReviewsCard';
import BannerIntroModal from '../components/BannerIntroModal';
import UpcomingInterviews from '../components/UpcomingInterviews';
import VerificationBanner from '../components/VerificationBanner';
import { useCredits } from '../lib/hooks';
import { useLiveUserJobs, useLiveUserGigs } from '../lib/realtime-hooks';
import { Skeleton } from '../components/ui/skeleton';
import { useOnboarding } from '../hooks/useOnboarding';
import { OnboardingTour, OnboardingChecklist } from '../components/onboarding';
import { readPendingVacancy, clearPendingVacancy, postPesoVacancy, applyEmployerMeta, claimPendingPost, releasePendingPost } from './peso/pesoVacancy';
import { usePricing } from '../hooks/usePricing';

const DashboardEmployer = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Check verification before allowing job posting
  // Always navigate to /post-job — it handles both pending and unverified states
  const handlePostJobClick = () => {
    navigate('/post-job');
  };

  // Helper to get job URL (use slug if available, otherwise ID)
  const getJobUrl = (job) => {
    return job.slug ? `/jobs/${job.slug}` : `/jobs/${job.id}`;
  };
  const [searchParams] = useSearchParams();
  const { pricing } = usePricing();
  const { credits, loading: creditsLoading, refetch: refetchCredits } = useCredits(profile);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [showApplicantDetail, setShowApplicantDetail] = useState(false);
  const [selectedReviewUser, setSelectedReviewUser] = useState(null);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [filterJobId, setFilterJobId] = useState('all');
  const [showMarkFilledDialog, setShowMarkFilledDialog] = useState(false);
  const [selectedJobToFill, setSelectedJobToFill] = useState(null);
  const [selectedHiredApplicants, setSelectedHiredApplicants] = useState([]);
  const [jobApplicants, setJobApplicants] = useState([]);
  const [jobsTab, setJobsTab] = useState('active'); // 'active' or 'expired'
  const [showPesoPendingModal, setShowPesoPendingModal] = useState(false);

  // PESO portal: if the employer registered via /PanglaoPESO but email confirmation
  // deferred login, their first vacancy was stashed — post it now (one-shot).
  useEffect(() => {
    if (!profile?.id) return;
    const pending = readPendingVacancy();
    if (!pending?.vacancy) return;
    if (!claimPendingPost()) return; // another mount is already handling it
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const token = session.access_token;
        if (pending.employerMeta) {
          try { await applyEmployerMeta(profile.id, pending.employerMeta, token); } catch (e) { /* non-fatal */ }
        }
        await postPesoVacancy(pending.vacancy, token);
        clearPendingVacancy(); // clear only after a confirmed save — failures retry on next load
        toast.success(t('peso.vacancyPendingApproval', 'Your PESO vacancy is saved and pending PESO approval.'));
      } catch (e) {
        console.warn('[PESO] deferred vacancy save failed (will retry on next visit):', e);
      } finally {
        releasePendingPost();
      }
    })();
  }, [profile?.id]);

  // PESO pending-approval MODAL: the bottom banner is easy to miss, so a PESO-registered
  // employer who is not yet approved gets a prominent pop-up on their dashboard. Shown once
  // per browser session (per user) so it stays visible over time without nagging on every
  // in-session navigation; the persistent amber banner remains as the always-on reminder.
  // Derive the pending condition here from `profile` directly (pesoPending is declared later
  // in the render body, so referencing it in a dep array would hit the TDZ).
  useEffect(() => {
    const isPeso = (typeof profile?.registration_source === 'string' && profile.registration_source.startsWith('peso_')) || !!profile?.peso_office;
    const pending = isPeso && !profile?.peso_approved;
    if (!pending || !profile?.id) return;
    try {
      if (sessionStorage.getItem('peso_pending_modal_seen_' + profile.id) !== 'true') {
        setShowPesoPendingModal(true);
      }
    } catch { /* sessionStorage unavailable — skip, banner still shows */ }
  }, [profile?.id, profile?.registration_source, profile?.peso_office, profile?.peso_approved]);

  const [unviewedCounts, setUnviewedCounts] = useState({});
  const [profileCompleteness, setProfileCompleteness] = useState(0);
  // Fetch applicants/bidders when a job/gig is selected for marking as filled
  useEffect(() => {
    const fetchJobApplicants = async () => {
      if (!selectedJobToFill) {
        setJobApplicants([]);
        return;
      }
      
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) return;

        // For gigs, fetch bids. For jobs, fetch applications
        const endpoint = selectedJobToFill.post_type === 'gig'
          ? `/api/bids/gig/${selectedJobToFill.id}`
          : `/api/applications/job/${selectedJobToFill.id}`;

        const response = await api.get(endpoint, {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });

        // Get the right data key
        const items = selectedJobToFill.post_type === 'gig' 
          ? response.data.bids 
          : response.data.applications;

        setJobApplicants(items || []);
      } catch (error) {
        console.error(`Failed to fetch ${selectedJobToFill.post_type === 'gig' ? 'bidders' : 'applicants'}:`, error);
        setJobApplicants([]);
      }
    };

    fetchJobApplicants();
  }, [selectedJobToFill]);

  // REALTIME: Use live jobs and gigs hooks for automatic updates
  const { jobs: rawJobs, loading: jobsLoading } = useLiveUserJobs(profile?.id);
  const { gigs: rawGigs, loading: gigsLoading } = useLiveUserGigs(profile?.id);

  // Merge jobs and gigs into a single list
  const myJobs = useMemo(() => {
    const jobsWithType = (rawJobs || []).map(job => ({ ...job, post_type: job.post_type || 'job' }));
    const gigsWithType = (rawGigs || []).map(gig => ({
      ...gig,
      post_type: 'gig',
      employer_id: gig.owner_id, // Map owner_id to employer_id for consistency
      salary_min: gig.budget_min,
      salary_max: gig.budget_max
    }));
    return [...jobsWithType, ...gigsWithType].sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    );
  }, [rawJobs, rawGigs]);
  
  // FIX ISSUE #2: Auto-refresh dashboard when returning from notifications
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refetch credits and profile when user returns to dashboard
        console.log('Dashboard visible - refreshing credits and profile');
        refetchCredits();
        
        // Refetch profile to update verification status
        const refreshProfile = async () => {
          try {
            const { data: session } = await supabase.auth.getSession();
            if (session?.session?.user) {
              const { data: profileData } = await supabase
                .from('profiles')
                .select('*')
                .eq('auth_user_id', session.session.user.id)
                .single();
              
              if (profileData) {
                setProfile(profileData);
              }
            }
          } catch (error) {
            console.error('Error refreshing profile:', error);
          }
        };
        refreshProfile();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetchCredits, setProfile]);
  
  // PESO-registered employers await PESO-staff approval; until then their posted jobs stay
  // as drafts (not public) and publish automatically once the account is approved.
  const isPesoEmployer = (typeof profile?.registration_source === 'string' && profile.registration_source.startsWith('peso_')) || !!profile?.peso_office;
  const pesoPending = isPesoEmployer && !profile?.peso_approved;

  // Filter jobs based on active/archived tab.
  // Expired jobs stay in the main list (with an "Expired" badge + a "Still hiring?" renew
  // button) so employers can revive them in one tap instead of them being hidden away — they're
  // already dropped from the public board by the expiry cron, so this is dashboard-only.
  const activeJobs = myJobs.filter(job => ['open', 'draft', 'expired'].includes(job.status));
  const archivedJobs = myJobs.filter(job => ['filled', 'closed', 'hired'].includes(job.status));

  // Onboarding stats for auto-completion detection
  const onboardingStats = useMemo(() => ({
    jobsCount: myJobs.length,
    filledJobsCount: archivedJobs.filter(j => j.status === 'filled' || j.status === 'hired').length,
    viewedApplicationsCount: applications.filter(a => a.viewed_at).length,
    profileCompleteness: profileCompleteness
  }), [myJobs, archivedJobs, applications, profileCompleteness]);

  // Onboarding hook
  const {
    tourSteps,
    checklistItems,
    completedItems,
    progress,
    allCompleted,
    showChecklist,
    shouldRunTour,
    tourStepIndex,
    startTour,
    handleChecklistDismiss,
    handleJoyrideCallback,
    tourJustFinished
  } = useOnboarding(profile, onboardingStats);

  // Filter applications by selected job
  const filteredApplications = filterJobId === 'all' 
    ? applications 
    : applications.filter(app => app.job_id === filterJobId);

  // Fetch profile completeness
  useEffect(() => {
    const fetchCompleteness = async () => {
      if (!profile?.id) return;
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) return;
        const response = await api.get('/api/profile/completeness', {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });
        setProfileCompleteness(response.data?.percentage || 0);
      } catch (error) {
        console.error('Error fetching profile completeness:', error);
      }
    };
    fetchCompleteness();
  }, [profile?.id]);

  // FIX #3: Parallelize data fetching with Promise.all
  useEffect(() => {
    const fetchAllData = async () => {
      if (!profile?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // FIX #3: Fetch applications in parallel
        const [applicationsResult] = await Promise.all([
          fetchApplicationsParallel()
        ]);

        setApplications(applicationsResult);

        // Fetch unviewed counts after applications are loaded
        fetchUnviewedCounts();
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    
    // REALTIME: Subscribe to applications changes
    if (profile?.id) {
      const channel = supabase
        .channel('employer-applications')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'applications'
          },
          (payload) => {
            console.log('🔄 Application change detected:', payload);
            fetchApplicationsParallel().then(setApplications);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [profile?.id, myJobs]);

  const fetchApplicationsParallel = async () => {
    if (!profile || myJobs.length === 0) {
      return [];
    }

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return [];

      const headers = { Authorization: `Bearer ${session.session.access_token}` };

      // FIX #3: Fetch all job applications and gig bids in parallel
      const applicationPromises = myJobs.map(job => {
        // For gigs, fetch bids. For jobs, fetch applications
        const endpoint = job.post_type === 'gig' 
          ? `/api/bids/gig/${job.id}`
          : `/api/applications/job/${job.id}`;
        
        return api.get(endpoint, { headers })
          .then(response => {
            const items = job.post_type === 'gig' 
              ? response.data.bids 
              : response.data.applications;
            
            console.log(`✅ Fetched ${job.post_type === 'gig' ? 'bids' : 'applications'} for ${job.post_type || 'job'} ${job.id} (${job.title}):`, items);
            
            return items?.map(item => ({
              ...item,
              // Preserve item.job_id from database if it exists, fallback to job.id
              job_id: item.job_id || job.id,
              job_title: job.title,
              post_type: job.post_type // Add post_type to know if it's a bid or application
            })) || [];
          })
          .catch(error => {
            console.error(`❌ Failed to fetch ${job.post_type === 'gig' ? 'bids' : 'applications'} for ${job.post_type || 'job'} ${job.id} (${job.title}):`, error);
            console.error('Error details:', error.response?.data);
            return [];
          });
      });

      const results = await Promise.all(applicationPromises);
      return results.flat();
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      return [];
    }
  };

  // Fetch unviewed application counts for all jobs
  const fetchUnviewedCounts = async () => {
    if (!profile || myJobs.length === 0) return;

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const headers = { Authorization: `Bearer ${session.session.access_token}` };
      
      const countPromises = myJobs.map(job => {
        const endpoint = job.post_type === 'gig'
          ? `/api/gigs/${job.id}/unviewed-bids-count`
          : `/api/jobs/${job.id}/unviewed-applications-count`;
        return api.get(endpoint, { headers })
          .then(response => ({ jobId: job.id, count: response.data.count }))
          .catch(() => ({ jobId: job.id, count: 0 }));
      });

      const results = await Promise.all(countPromises);
      const counts = results.reduce((acc, { jobId, count }) => {
        acc[jobId] = count;
        return acc;
      }, {});
      
      setUnviewedCounts(counts);
    } catch (error) {
      console.error('Failed to fetch unviewed counts:', error);
    }
  };

  const handleMarkJobFilled = async () => {
    if (!selectedJobToFill) return;

    try {
      const { data: session } = await supabase.auth.getSession();
      const isGig = selectedJobToFill.post_type === 'gig';

      // Use different endpoints and parameter names for jobs vs gigs
      const endpoint = isGig
        ? `/api/gigs/${selectedJobToFill.id}/mark-filled`
        : `/api/jobs/${selectedJobToFill.id}/mark-filled`;

      // Support multi-hire: send array of hired applicant IDs for jobs
      const requestBody = isGig
        ? { hired_contractor_id: selectedHiredApplicants[0] || null }  // Gigs still use single hire
        : { hired_applicant_ids: selectedHiredApplicants };  // Jobs use multi-hire array

      await api.post(endpoint, requestBody, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      const hiredCount = selectedHiredApplicants.length;
      if (hiredCount > 0) {
        toast.success(`${isGig ? 'Gig' : 'Job'} marked as filled! ${hiredCount} ${isGig ? 'bidder' : 'applicant'}${hiredCount > 1 ? 's have' : ' has'} been notified. You can review them anytime from your dashboard.`);
      } else {
        toast.success(`${isGig ? 'Gig' : 'Job'} marked as filled! All ${isGig ? 'bidders' : 'applicants'} have been notified.`);
      }

      setShowMarkFilledDialog(false);
      setSelectedJobToFill(null);
      setSelectedHiredApplicants([]);
      setJobApplicants([]);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to mark ${selectedJobToFill.post_type === 'gig' ? 'gig' : 'job'} as filled`);
      setShowMarkFilledDialog(false);
    }
  };

  const markAsViewed = async (applicationId, seekerId, postType) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const endpoint = postType === 'gig'
        ? `/api/bids/${applicationId}/view`
        : `/api/applications/${applicationId}/view`;

      await api.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      // Update local state immediately to reflect viewed status
      setApplications(prevApps =>
        prevApps.map(app =>
          app.id === applicationId
            ? { ...app, viewed_at: new Date().toISOString() }
            : app
        )
      );
      // Refresh unviewed counts on job cards
      fetchUnviewedCounts();
    } catch (error) {
      console.error('Failed to mark as viewed:', error);
    }
  };

  const renderApplicationRow = (application, index) => {
    // For bids, use contractor. For applications, use seeker
    const applicant = application.contractor || application.seeker;
    const isNew = !application.viewed_at;
    
    return (
      <tr 
        key={application.id}
        className={`hover:bg-muted/50 cursor-pointer transition-colors ${isNew ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}
        onClick={() => {
          setSelectedApplicant(application);
          setShowApplicantDetail(true);
          markAsViewed(application.id, applicant?.id, application.post_type);
        }}
        data-testid="application-row"
      >
        {/* Status Indicator */}
        <td className="py-3 px-1 md:px-4">
          {isNew ? (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0" title="New - Not viewed yet" />
              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800 hidden sm:inline-flex">{t('dashboard.new')}</Badge>
            </div>
          ) : (
            <Badge variant="outline" className="text-xs text-muted-foreground hidden sm:inline-flex">{t('dashboard.read')}</Badge>
          )}
        </td>
        
        {/* Applicant */}
        <td className="py-3 px-2 md:px-4">
          <div className="flex items-center gap-2 md:gap-3">
            <Avatar className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0">
              <AvatarFallback className="text-xs md:text-sm">
                {applicant?.name?.[0] || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="font-medium text-xs md:text-sm flex items-center gap-1 md:gap-2">
                <span className="truncate">{applicant?.name}</span>
                {applicant?.verified && (
                  <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {applicant?.town}
                {applicant?.rating > 0 && (
                  <span className="ml-2 inline-flex items-center gap-1 text-yellow-600">
                    <Star size={10} className="fill-yellow-500" />
                    {applicant.rating.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>
        
        {/* Skills - Hidden on mobile */}
        <td className="py-3 px-2 md:px-4 hidden md:table-cell">
          <div className="flex flex-wrap gap-1 max-w-[140px] md:max-w-none">
            {applicant?.skills?.slice(0, 3).map((skill, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs truncate max-w-[120px]" title={skill}>
                {skill}
              </Badge>
            )) || <span className="text-xs text-muted-foreground">{t('dashboard.noSkillsListed')}</span>}
          </div>
        </td>
        
        {/* Job - Hidden on mobile and small tablets */}
        <td className="py-3 px-2 md:px-4 max-w-[200px] hidden lg:table-cell">
          <div className="text-sm font-medium truncate">{application.job_title}</div>
        </td>
        
        {/* Applied Date - Hidden on smallest screens */}
        <td className="py-3 px-2 md:px-4 hidden sm:table-cell">
          <div className="text-xs text-muted-foreground whitespace-nowrap">
            {new Date(application.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        </td>
        
        {/* Actions */}
        <td className="py-3 px-2 md:px-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1 justify-end">
            <Button
              size="sm"
              variant="outline"
              className="rounded-xl text-xs whitespace-nowrap"
              onClick={(e) => {
                e.stopPropagation();
                // messaging the applicant counts as seeing them (no "waiting" nudge)
                if (isNew) markAsViewed(application.id, applicant?.id, application.post_type);
                navigate(`/messages?user=${applicant?.id}`);
              }}
              data-testid="chat-button"
            >
              <MessageCircle size={14} className="mr-1" />
              <span className="hidden sm:inline">{t('dashboard.chat')}</span>
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />
      {pricing.banner_show_popup && (
        <BannerIntroModal profile={profile} maxShows={pricing.banner_popup_max_shows} />
      )}

      <div className="px-4 py-6 max-w-7xl mx-auto">
        {/* Hero Section */}
        <div data-tour="employer-hero" className="bg-gradient-to-r from-primary to-primary/70 rounded-2xl p-4 md:p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-4xl font-bold mb-2 break-words">{t('dashboard.hiringManagerDashboard')}</h1>
              <p className="text-white/90 text-sm md:text-lg">{t('dashboard.manageApplications')}</p>
            </div>
            {/* Credits Widget - Mobile Optimized */}
            <div data-tour="employer-credits" className="flex-shrink-0 w-full md:w-auto">
              <div className="text-center bg-white/20 backdrop-blur rounded-2xl p-4 md:p-6 max-w-[200px] mx-auto md:mx-0">
                <Coins size={32} className="mx-auto mb-2 md:w-10 md:h-10" />
                {creditsLoading ? (
                  <div className="animate-pulse">
                    <div className="h-10 md:h-12 w-16 bg-white/30 rounded mx-auto mb-2" />
                    <div className="h-3 md:h-4 w-20 bg-white/30 rounded mx-auto" />
                  </div>
                ) : (
                  <>
                    <div className="text-3xl md:text-5xl font-bold">{credits.balance}</div>
                    <p className="text-xs md:text-sm text-white/80 mt-1">{t('dashboard.credits')}</p>
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      className="mt-2 md:mt-3 rounded-xl w-full text-xs md:text-sm" 
                      onClick={() => navigate('/credits')}
                    >
                      {credits.balance === 0 ? t('dashboard.buyCredits') : t('dashboard.buyMore')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-6 flex gap-3 flex-wrap items-center">
          <Button
            data-tour="employer-post-job"
            size="lg"
            onClick={handlePostJobClick}
            className="rounded-xl text-base md:text-lg py-4 md:py-6 px-5 md:px-8 w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600"
          >
            <Plus size={24} className="mr-2" />
            {t('dashboard.postNewJob')}
          </Button>
        </div>

        {/* Verification Banner */}
        <div data-tour="employer-verify-id">
          <VerificationBanner
            profile={profile}
            onVerifyClick={() => navigate('/verify-id')}
          />
        </div>

        {/* Referral Program */}
        <div data-tour="employer-referral" className="mb-8">
          <ReferralSection profile={profile} />
        </div>

        {/* Upcoming Interviews */}
        <div className="mb-8">
          <UpcomingInterviews isEmployer={true} limit={5} />
        </div>

        {/* PESO pending-approval notice: make it clear drafts are NOT live yet */}
        {pesoPending && (
          <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 md:p-5">
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <div className="text-sm">
                <p className="font-semibold text-amber-900">{t('dashboard.pesoPendingTitle', 'Your account is pending PESO review')}</p>
                <p className="mt-1 text-amber-800">
                  {t('dashboard.pesoPendingBody', 'Any job you post is saved as a draft and is NOT public yet. Once PESO staff verify and approve your account, your job will be published automatically — no further action is needed from you.')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* My Job Postings */}
        <div data-tour="employer-postings" data-section="postings" className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-2xl font-semibold">{t('dashboard.myJobPostings')} ({myJobs.length})</h2>
          </div>

          <Tabs value={jobsTab} onValueChange={setJobsTab} className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2 mb-4">
              <TabsTrigger value="active" className="rounded-xl text-xs sm:text-sm">
                {t('dashboard.active')} ({activeJobs.length})
              </TabsTrigger>
              <TabsTrigger value="expired" className="rounded-xl text-xs sm:text-sm">
                {t('dashboard.filledClosed', 'Filled & closed')} ({archivedJobs.length})
              </TabsTrigger>
            </TabsList>

            {/* Active Jobs Tab */}
            <TabsContent value="active">
              {jobsLoading ? (
                <Card>
                  <Skeleton className="h-64" />
                </Card>
              ) : activeJobs.length === 0 ? (
                <Card className="p-12 text-center">
                  <Briefcase className="mx-auto mb-4 text-muted-foreground" size={48} />
                  <h3 className="font-heading text-xl font-semibold mb-2">{t('dashboard.noActiveJobs')}</h3>
                  <p className="text-muted-foreground mb-4">{t('dashboard.postFirstJobDesc')}</p>
                  <Button onClick={() => navigate('/post-job')} className="rounded-xl">{t('dashboard.postYourFirstJob')}</Button>
                </Card>
              ) : (
                <JobPostingsTable
                  jobs={activeJobs}
                  pesoPending={pesoPending}
                  isArchived={false}
                  refetchCredits={refetchCredits}
                  applicationsCount={applications.reduce((acc, app) => {
                    acc[app.job_id] = (acc[app.job_id] || 0) + 1;
                    return acc;
                  }, {})}
                  unviewedCounts={unviewedCounts}
                  onMarkFilled={(job) => {
                    setSelectedJobToFill(job);
                    setShowMarkFilledDialog(true);
                  }}
                  onDeleteDraft={() => window.location.reload()}
                />
              )}
            </TabsContent>

            {/* Expired Jobs Tab */}
            <TabsContent value="expired">
              {jobsLoading ? (
                <Card>
                  <Skeleton className="h-64" />
                </Card>
              ) : archivedJobs.length === 0 ? (
                <Card className="p-12 text-center">
                  <Briefcase className="mx-auto mb-4 text-muted-foreground" size={48} />
                  <h3 className="font-heading text-xl font-semibold mb-2">{t('dashboard.noFilledClosedJobs', 'Nothing here yet')}</h3>
                  <p className="text-muted-foreground">{t('dashboard.filledClosedJobsDesc', 'Jobs you mark as filled or closed will appear here. Expired jobs stay in your active list so you can renew them.')}</p>
                </Card>
              ) : (
                <JobPostingsTable 
                  jobs={archivedJobs}
                  isArchived={true}
                  refetchCredits={refetchCredits}
                  applicationsCount={applications.reduce((acc, app) => {
                    acc[app.job_id] = (acc[app.job_id] || 0) + 1;
                    return acc;
                  }, {})}
                  unviewedCounts={unviewedCounts}
                  onMarkFilled={(job) => {
                    setSelectedJobToFill(job);
                    setShowMarkFilledDialog(true);
                  }}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Old card-based code removed */}

        {/* Applications section removed - now shown on individual job details pages */}

        {/* My Banners (homepage carousel placements) */}
        {pricing.banner_show_my_banners && (
          <div className="mb-8">
            <MyBannersSection profile={profile} />
          </div>
        )}

        {/* My Services (Services Marketplace listings) — self-hides when the flag is off */}
        <div className="mb-8">
          <MyServiceListingsSection profile={profile} />
        </div>

        {/* My service requests (client "I need X" posts) — self-hides when the flag is off */}
        <div className="mb-8">
          <MyServiceRequestsSection profile={profile} />
        </div>

        {/* Matching requests for Pros — self-hides when flag off or no active listing */}
        <div className="mb-8">
          <MatchingRequestsSection profile={profile} />
        </div>

        {/* Services action queue: hire confirmations + pending reviews — self-hides when flag off/empty */}
        <div className="mb-8">
          <PendingReviewsCard profile={profile} />
        </div>

        {/* My Credentials — self-hides when the flag is off */}
        <div className="mb-8">
          <ProCredentialsSection profile={profile} />
        </div>

        {/* Past Hires Section */}
        <div className="mb-8">
          <PastHiresSection profile={profile} />
        </div>
      </div>

      <ReviewsListModal
        open={showReviewsModal}
        onOpenChange={setShowReviewsModal}
        userId={selectedReviewUser?.id}
        userName={selectedReviewUser?.name}
      />

      <ApplicantDetailModal
        open={showApplicantDetail}
        onOpenChange={(open) => {
          setShowApplicantDetail(open);
        }}
        applicant={selectedApplicant}
        job={selectedApplicant ? (
          myJobs.find(j => j.id === selectedApplicant.job_id) ||
          // Fallback: construct minimal job object from application data
          (selectedApplicant.job_id ? { id: selectedApplicant.job_id, title: selectedApplicant.job_title || 'Job' } : null)
        ) : null}
        revealed={true}
      />

      {/* Mark as Filled Dialog with Applicant Selection */}
      <Dialog open={showMarkFilledDialog} onOpenChange={(open) => {
        setShowMarkFilledDialog(open);
        if (!open) {
          setSelectedJobToFill(null);
          setSelectedHiredApplicants([]);
          setJobApplicants([]);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedJobToFill?.post_type === 'gig' ? t('dashboard.markGigFilled') : t('dashboard.markJobFilled')}
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-2">
              {selectedJobToFill && (
                <div className="bg-muted p-3 rounded-lg">
                  <p className="font-medium text-foreground">{selectedJobToFill.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{selectedJobToFill.location}</p>
                  {selectedJobToFill.positions_available > 1 && (
                    <p className="text-sm text-purple-600 mt-1 font-medium">
                      Hiring {selectedJobToFill.positions_available} positions
                    </p>
                  )}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {jobApplicants.length > 0 ? (
              <>
                <p className="text-sm font-medium mb-3">
                  {selectedJobToFill?.post_type === 'gig'
                    ? t('dashboard.whichBidderHired')
                    : selectedJobToFill?.positions_available > 1
                      ? 'Select all applicants you hired (multiple allowed)'
                      : t('dashboard.whichApplicantHired')}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  {selectedJobToFill?.post_type !== 'gig' && selectedJobToFill?.positions_available > 1
                    ? 'Check the boxes for each person you hired. All selected will be notified and marked as hired.'
                    : t('dashboard.selectHiredPersonDesc')}
                </p>

                {selectedHiredApplicants.length > 0 && (
                  <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">
                      {selectedHiredApplicants.length} applicant{selectedHiredApplicants.length > 1 ? 's' : ''} selected
                    </p>
                  </div>
                )}

                <div className="space-y-2 max-h-[300px] overflow-y-auto border rounded-lg p-2">
                  {/* Applicant/Bidder options */}
                  {jobApplicants.map((app) => {
                    // For bids use contractor, for applications use seeker
                    const applicant = app.contractor || app.seeker;
                    const applicantId = app.contractor_id || app.seeker_id;
                    const isSelected = selectedHiredApplicants.includes(applicantId);
                    const isGig = selectedJobToFill?.post_type === 'gig';
                    const isMultiHire = !isGig && selectedJobToFill?.positions_available > 1;

                    // Multi-select (checkboxes) only for jobs with positions_available > 1
                    // Single-select (radio) for gigs and jobs with positions_available = 1 or undefined
                    const handleClick = () => {
                      if (isMultiHire) {
                        // Multi-select for multi-hire jobs
                        setSelectedHiredApplicants(prev =>
                          isSelected
                            ? prev.filter(id => id !== applicantId)
                            : [...prev, applicantId]
                        );
                      } else {
                        // Single select for gigs and single-position jobs
                        setSelectedHiredApplicants(isSelected ? [] : [applicantId]);
                      }
                    };

                    return (
                      <div
                        key={app.id}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={handleClick}
                        data-testid={`select-applicant-${applicantId}`}
                      >
                        <div className="flex items-center gap-3">
                          {/* Checkbox for multi-hire jobs, radio for gigs and single-position jobs */}
                          {isMultiHire ? (
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected ? 'border-primary bg-primary' : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <CheckCircle size={14} className="text-white" />
                              )}
                            </div>
                          ) : (
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? 'border-primary bg-primary' : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <div className="w-2 h-2 rounded-full bg-white" />
                              )}
                            </div>
                          )}
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {applicant?.name?.[0] || 'A'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{applicant?.name || 'Anonymous'}</p>
                            <p className="text-xs text-muted-foreground">{applicant?.town || 'Location not specified'}</p>
                            {app.amount && (
                              <p className="text-xs text-primary font-semibold mt-1">Bid: ₱{app.amount.toLocaleString()}</p>
                            )}
                            {applicant?.rating > 0 && (
                              <div className="flex items-center gap-1 mt-1">
                                <Star size={12} className="fill-yellow-500 text-yellow-500" />
                                <span className="text-xs text-yellow-600">{applicant.rating.toFixed(1)}</span>
                              </div>
                            )}
                          </div>
                          <Badge variant={app.status === 'accepted' ? 'default' : 'secondary'} className="text-xs">
                            {app.status === 'shortlisted' ? t('dashboard.movingForward', 'Moving forward')
                              : app.status === 'rejected' ? t('dashboard.notAFit', 'Not a fit this time')
                              : app.status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <p className="text-sm">
                  {selectedJobToFill?.post_type === 'gig' ? t('dashboard.noBidsYet') : t('dashboard.noApplicantsYet')}
                </p>
                <p className="text-xs mt-2">
                  {t('dashboard.markedFilledRemoved', { type: selectedJobToFill?.post_type === 'gig' ? 'gig' : 'trabaho' })}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setShowMarkFilledDialog(false);
                setSelectedJobToFill(null);
                setSelectedHiredApplicants([]);
                setJobApplicants([]);
              }}
              className="flex-1"
            >
              {t('dashboard.cancel')}
            </Button>
            <Button
              onClick={handleMarkJobFilled}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-mark-filled"
            >
              <CheckCircle size={16} className="mr-2" />
              {selectedHiredApplicants.length > 0
                ? `Mark Filled (${selectedHiredApplicants.length} hired)`
                : t('dashboard.markAsFilled')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PESO pending-approval MODAL — prominent version of the amber banner so a newly
          registered PESO employer clearly understands their jobs are drafts until approved. */}
      <Dialog
        open={showPesoPendingModal}
        onOpenChange={(open) => {
          if (!open) {
            try { sessionStorage.setItem('peso_pending_modal_seen_' + (profile?.id || ''), 'true'); } catch { /* ignore */ }
          }
          setShowPesoPendingModal(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
              <Clock className="h-7 w-7 text-amber-600" />
            </div>
            <DialogTitle className="text-center text-lg">
              {t('dashboard.pesoPendingTitle', 'Your account is pending PESO review')}
            </DialogTitle>
            <DialogDescription className="text-center pt-1 text-sm leading-relaxed">
              {t('dashboard.pesoPendingBody', 'Any job you post is saved as a draft and is NOT public yet. Once PESO staff verify and approve your account, your job will be published automatically — no further action is needed from you.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => {
                // Persist the "seen" flag here too: a controlled Radix Dialog does NOT fire
                // onOpenChange when we flip `open` via setState, so this primary dismiss path
                // must write the flag itself or the modal would re-pop on the next remount.
                try { sessionStorage.setItem('peso_pending_modal_seen_' + (profile?.id || ''), 'true'); } catch { /* ignore */ }
                setShowPesoPendingModal(false);
              }}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="peso-pending-ack"
            >
              {t('dashboard.pesoPendingAck', 'Got it')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FloatingActionButton
        onClick={handlePostJobClick}
        icon={Plus}
        label={t('dashboard.postOpening')}
      />
      
      {/* Verification handled by /verify-id page */}

      {/* Need Help Section */}
      <div className="container mx-auto px-4 pb-nav md:pb-8 mt-8">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 text-center border border-blue-200">
          <h3 className="text-lg font-semibold mb-2">Need Help?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Have questions or need support? Our team is here to help!
          </p>
          <ContactUsButton variant="default" className="bg-blue-600 hover:bg-blue-700" />
        </div>
      </div>

      <BottomNav profile={profile} />

      {/* Onboarding Tour */}
      <OnboardingTour
        steps={tourSteps}
        run={shouldRunTour}
        stepIndex={tourStepIndex}
        onCallback={handleJoyrideCallback}
      />

      {/* Onboarding Checklist */}
      {showChecklist && (
        <OnboardingChecklist
          items={checklistItems}
          completedItems={completedItems}
          progress={progress}
          onDismiss={handleChecklistDismiss}
          onRestartTour={startTour}
          allCompleted={allCompleted}
          defaultMinimized={true}
        />
      )}
    </div>
  );
};

export default DashboardEmployer;